// src/pages/AdminSidebarItemsPage.jsx 
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";

import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

const MAX_ICON_SIZE = 300 * 1024; // 300 KB
const ICON_BUCKET = "assets";

// helper: bikin route dari key → /dashboard/key-yang-sudah-dislug
const makeRouteFromKey = (key) => {
  const safeKey = (key || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  if (!safeKey) return "/dashboard";
  return `/dashboard/${safeKey}`;
};

// helper: konversi path "uploads/xxx.png" → public URL image
const getIconPublicUrl = (path) => {
  if (!path) return null;
  const { data } = supabase.storage.from(ICON_BUCKET).getPublicUrl(path);
  return data?.publicUrl || null;
};

const AdminSidebarItemsPage = () => {
  const navigate = useNavigate();

  // auth/admin
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  // data
  const [features, setFeatures] = useState([]);
  const [sidebarItems, setSidebarItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [editingItem, setEditingItem] = useState(null);

  // form (create & edit)
  const [newItem, setNewItem] = useState({
    key: "",
    label: "",
    icon_url: "",
    feature_code: "",
    feature_name: "",
    feature_description: "",
    is_active: true,
  });

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate("/login");
          return;
        }
        setUser(authUser);

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") {
          throw profileError;
        }

        if (!profileData || !profileData.phone) {
          navigate("/login");
          return;
        }

        if (profileData.role !== "admin") {
          navigate("/dashboard");
          return;
        }

        setProfile(profileData);

        const [
          { data: featuresData, error: featuresError },
          { data: sidebarData, error: sidebarError },
        ] = await Promise.all([
          supabase
            .from("features")
            .select("*")
            .order("code", { ascending: true }),
          supabase
            .from("sidebar_items")
            .select("*")
            .order("sort_order", { ascending: true })
            .order("id", { ascending: true }),
        ]);

        if (featuresError) throw featuresError;
        if (sidebarError) throw sidebarError;

        setFeatures(featuresData || []);
        setSidebarItems(sidebarData || []);
      } catch (err) {
        console.error("Error loading admin sidebar items page:", err);
        setError("Gagal memuat data. Coba lagi beberapa saat.");
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [navigate]);

  // ==== MODAL HELPERS ====
  const openCreateModal = () => {
    setModalMode("create");
    setEditingItem(null);
    setNewItem({
      key: "",
      label: "",
      icon_url: "",
      feature_code: "",
      feature_name: "",
      feature_description: "",
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    const feature = features.find((f) => f.code === item.feature_code);
    setModalMode("edit");
    setEditingItem(item);
    setNewItem({
      key: item.key || "",
      label: item.label || "",
      icon_url: item.icon_url || "",
      feature_code: item.feature_code || "",
      feature_name: feature?.name || "",
      feature_description: feature?.description || "",
      is_active: item.is_active,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  // ==== HANDLER: upload icon (create & edit) ====
  const handleIconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("File icon harus berupa gambar.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_ICON_SIZE) {
      alert("Ukuran icon maksimal 300 KB.");
      e.target.value = "";
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const ext = file.name.split(".").pop();
      const fileName = `sidebar-icon-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}.${ext || "png"}`;

      const filePath = `uploads/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(ICON_BUCKET)
        .upload(filePath, file);

      if (uploadError) {
        console.error(uploadError);
        if (
          typeof uploadError.message === "string" &&
          uploadError.message.toLowerCase().includes("bucket not found")
        ) {
          throw new Error(
            `Bucket "${ICON_BUCKET}" tidak ditemukan. Buat bucket ini dulu di Supabase Storage atau ganti nama bucket di kode.`
          );
        }
        throw uploadError;
      }

      setNewItem((prev) => ({
        ...prev,
        icon_url: filePath,
      }));
    } catch (err) {
      console.error("Error upload icon:", err);
      setError(err.message || "Gagal meng-upload icon.");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  // ==== HANDLER: submit (create & edit) ====
  const handleSubmitSidebarItem = async (e) => {
    e.preventDefault();

    if (!newItem.key.trim() || !newItem.label.trim()) {
      alert("Key dan Label wajib diisi.");
      return;
    }
    if (!newItem.feature_code.trim() || !newItem.feature_name.trim()) {
      alert("Feature code dan nama fitur wajib diisi.");
      return;
    }

    const featureCode = newItem.feature_code.trim().toUpperCase();
    const featureName = newItem.feature_name.trim();
    const featureDesc = newItem.feature_description.trim() || null;
    const keyTrimmed = newItem.key.trim();
    const routePath = makeRouteFromKey(keyTrimmed);

    try {
      setSaving(true);
      setError(null);

      if (modalMode === "create") {
        // pastikan fitur ada
        const existing = features.find((f) => f.code === featureCode);
        if (!existing) {
          const { data: insertedFeature, error: featureError } = await supabase
            .from("features")
            .insert({
              code: featureCode,
              name: featureName,
              description: featureDesc,
            })
            .select()
            .single();

          if (featureError) throw featureError;
          setFeatures((prev) => [...prev, insertedFeature]);
        }

        // sort_order
        const maxSort = sidebarItems.reduce((max, item) => {
          const so = typeof item.sort_order === "number" ? item.sort_order : 0;
          return so > max ? so : max;
        }, 0);

        const payload = {
          key: keyTrimmed,
          label: newItem.label.trim(),
          icon_url: newItem.icon_url || null,
          route_path: routePath,
          feature_code: featureCode,
          sort_order: maxSort + 1,
          is_active: newItem.is_active,
        };

        const { data, error: insertError } = await supabase
          .from("sidebar_items")
          .insert(payload)
          .select()
          .single();

        if (insertError) throw insertError;

        setSidebarItems((prev) => [...prev, data]);
        setNewItem({
          key: "",
          label: "",
          icon_url: "",
          feature_code: "",
          feature_name: "",
          feature_description: "",
          is_active: true,
        });
        setIsModalOpen(false);
      } else if (modalMode === "edit" && editingItem) {
        // update / buat feature
        const existingFeature = features.find((f) => f.code === featureCode);

        if (existingFeature) {
          const { data: updatedFeature, error: featureUpdateError } =
            await supabase
              .from("features")
              .update({
                name: featureName,
                description: featureDesc,
              })
              .eq("code", featureCode)
              .select()
              .single();

          if (featureUpdateError) throw featureUpdateError;

          setFeatures((prev) =>
            prev.map((f) => (f.code === featureCode ? updatedFeature : f))
          );
        } else {
          const { data: insertedFeature, error: featureInsertError } =
            await supabase
              .from("features")
              .insert({
                code: featureCode,
                name: featureName,
                description: featureDesc,
              })
              .select()
              .single();

          if (featureInsertError) throw featureInsertError;
          setFeatures((prev) => [...prev, insertedFeature]);
        }

        const payload = {
          key: keyTrimmed,
          label: newItem.label.trim(),
          icon_url: newItem.icon_url || null,
          route_path: routePath,
          feature_code: featureCode,
          is_active: newItem.is_active,
        };

        const { data: updatedItem, error: updateError } = await supabase
          .from("sidebar_items")
          .update(payload)
          .eq("id", editingItem.id)
          .select()
          .single();

        if (updateError) throw updateError;

        setSidebarItems((prev) =>
          prev.map((it) => (it.id === editingItem.id ? updatedItem : it))
        );

        setIsModalOpen(false);
        setEditingItem(null);
      }
    } catch (err) {
      console.error("Error submit sidebar item (create/edit):", err);
      setError(
        err.message ||
          "Gagal menyimpan sidebar item. Lihat console untuk detail."
      );
    } finally {
      setSaving(false);
    }
  };

  // ==== HANDLER: hapus item (+ bersihkan features & user_features) ====
  const handleDeleteSidebarItem = async (id) => {
    const item = sidebarItems.find((it) => it.id === id);
    if (!item) return;

    const featureCode = item.feature_code;

    let message = "Hapus item sidebar ini?";
    if (featureCode) {
      message +=
        `\n\nCatatan:` +
        `\n- Semua baris di tabel user_features dengan feature_code "${featureCode}" akan dihapus.` +
        `\n- Jika tidak ada sidebar item lain yg memakai feature_code ini, baris di tabel features juga akan dihapus.`;
    }

    if (!window.confirm(message)) return;

    try {
      setSaving(true);
      setError(null);

      if (featureCode) {
        const { error: ufError } = await supabase
          .from("user_features")
          .delete()
          .eq("feature_code", featureCode);

        if (ufError) {
          console.error("Error deleting user_features:", ufError);
          throw new Error(
            `Gagal menghapus user_features untuk feature ${featureCode}: ${ufError.message}`
          );
        }
      }

      const { error: deleteSidebarError } = await supabase
        .from("sidebar_items")
        .delete()
        .eq("id", id);

      if (deleteSidebarError) {
        console.error("Error deleting sidebar item:", deleteSidebarError);
        throw deleteSidebarError;
      }

      setSidebarItems((prev) => prev.filter((it) => it.id !== id));

      if (featureCode) {
        const stillUsed = sidebarItems.some(
          (it) => it.id !== id && it.feature_code === featureCode
        );

        if (!stillUsed) {
          const { error: deleteFeatureError } = await supabase
            .from("features")
            .delete()
            .eq("code", featureCode);

          if (deleteFeatureError) {
            console.warn(
              "Error deleting feature (mungkin masih dipakai di package_features):",
              deleteFeatureError
            );
          } else {
            setFeatures((prev) =>
              prev.filter((f) => f.code !== featureCode)
            );
          }
        }
      }
    } catch (err) {
      console.error("Error deleting sidebar item / feature:", err);
      setError(
        err.message ||
          "Gagal menghapus item sidebar / fitur. Lihat console untuk detail."
      );
    } finally {
      setSaving(false);
    }
  };

  // ==== HANDLER: toggle aktif ====
  const handleToggleActive = async (itemId, currentValue) => {
    try {
      setSaving(true);
      setError(null);

      const { data, error: updateError } = await supabase
        .from("sidebar_items")
        .update({ is_active: !currentValue })
        .eq("id", itemId)
        .select()
        .single();

      if (updateError) throw updateError;

      setSidebarItems((prev) =>
        prev.map((item) => (item.id === itemId ? data : item))
      );
    } catch (err) {
      console.error("Error toggling sidebar item active flag:", err);
      setError("Gagal mengubah status aktif item.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mb-3" />
        <p className="text-slate-500 text-sm">
          Memuat konfigurasi sidebar items...
        </p>
      </div>
    );
  }

  const displayName =
    profile?.username || user?.email?.split("@")[0] || "Admin";

  const autoRoutePreview = makeRouteFromKey(newItem.key);
  const newIconPreview = getIconPublicUrl(newItem.icon_url);

  return (
    <div className="h-screen bg-slate-50 font-sans overflow-hidden">
      <div className="flex h-full min-w-0">
        {/* SIDEBAR ADMIN */}
        <DashboardSidebarAdmin />

        {/* AREA KANAN */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 relative">
          <DashboardNavbarAdmin displayName={displayName} />

          <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-5 sm:p-6">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                  Sidebar Items
                </h1>
                <p className="text-sm sm:text-base text-slate-600">
                  Admin bisa membuat fitur + item sidebar sekaligus. Item hanya
                  akan tampil ke user yang punya fitur tersebut (via paket /
                  voucher → <code>userFeatures</code>), jadi user yang belum
                  subscribe tidak akan melihat menu ini.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                  {error}
                </div>
              )}

              <section className="bg-white rounded-2xl shadow-sm border border-slate-200/70 p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-sm sm:text-base font-semibold text-slate-800">
                      Daftar Sidebar Items
                    </h2>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Atur menu sidebar, ubah label, icon, dan status aktif.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openCreateModal}
                    className="
                      inline-flex items-center justify-center
                      px-3 h-8 rounded-lg text-[12px] font-semibold
                      bg-sky-600 text-white
                      hover:bg-sky-700
                      transition
                    "
                  >
                    + Tambah Sidebar Item
                  </button>
                </div>

                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {sidebarItems.map((item) => {
                    const feature = features.find(
                      (f) => f.code === item.feature_code
                    );
                    const iconUrl = getIconPublicUrl(item.icon_url);

                    return (
                      <div
                        key={item.id}
                        className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt=""
                              className="w-8 h-8 rounded-xl object-contain bg-slate-50 border border-slate-200 flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-xl bg-sky-50 border border-sky-100 text-sky-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                              {item.label?.charAt(0) || "?"}
                            </div>
                          )}

                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] font-semibold text-slate-900 truncate">
                                {item.label}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                key: {item.key}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              <code className="px-1.5 py-0.5 rounded-full bg-slate-50 border border-slate-200">
                                {item.route_path}
                              </code>
                            </div>
                            {typeof item.sort_order === "number" && (
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Urutan: {item.sort_order}
                              </div>
                            )}
                            {item.icon_url && (
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Path icon:{" "}
                                <code className="bg-slate-100 px-1 py-0.5 rounded">
                                  {item.icon_url}
                                </code>
                              </div>
                            )}
                            {feature ? (
                              <div className="text-[10px] text-slate-500 mt-1">
                                Feature:&nbsp;
                                <span className="font-semibold text-sky-700">
                                  {feature.code}
                                </span>{" "}
                                – {feature.name}
                              </div>
                            ) : item.feature_code ? (
                              <div className="text-[10px] text-amber-600 mt-1">
                                Feature code tidak ditemukan di tabel
                                <code> features</code>:{" "}
                                <span className="font-semibold">
                                  {item.feature_code}
                                </span>
                              </div>
                            ) : (
                              <div className="text-[10px] text-amber-600 mt-1">
                                Tidak ada <code>feature_code</code> – menu ini
                                akan tampil ke semua user.
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(item)}
                              className="
                                inline-flex items-center justify-center
                                px-2.5 h-7 rounded-full text-[11px] font-semibold
                                bg-slate-50 text-slate-700 border border-slate-200
                                hover:bg-slate-100
                              "
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                handleToggleActive(item.id, item.is_active)
                              }
                              disabled={saving}
                              className={`
                                inline-flex items-center justify-center
                                px-2.5 h-7 rounded-full text-[11px] font-semibold
                                border
                                ${
                                  item.is_active
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-slate-50 text-slate-500 border-slate-200"
                                }
                              `}
                            >
                              {item.is_active ? "Aktif" : "Nonaktif"}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteSidebarItem(item.id)}
                              disabled={saving}
                              className="text-[11px] text-red-500 hover:text-red-600"
                            >
                              Hapus
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {sidebarItems.length === 0 && (
                    <p className="text-[12px] text-slate-500">
                      Belum ada sidebar item di database.
                    </p>
                  )}
                </div>

                {saving && (
                  <div className="text-[11px] text-slate-400 mt-2">
                    Menyimpan perubahan...
                  </div>
                )}
              </section>
            </div>
          </main>

          {/* MODAL: sekarang di dalam area kanan & absolute, tidak fixed full-screen */}
          {isModalOpen && (
            <div className="absolute inset-0 z-[60] flex items-center justify-center bg-slate-900/40">
              <form
                onSubmit={handleSubmitSidebarItem}
                className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-5xl max-h-[80vh] flex flex-col"
              >
                {/* Header modal */}
                <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm sm:text-base font-semibold text-slate-800">
                      {modalMode === "create"
                        ? "Tambah Sidebar Item"
                        : "Edit Sidebar Item"}
                    </h2>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Atur informasi sidebar item dan fiturnya.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="text-slate-400 hover:text-slate-600 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>

                {/* Body modal */}
                <div className="p-4 sm:p-5 overflow-y-auto space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Key */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-600">
                        Key (unik)
                      </label>
                      <input
                        type="text"
                        className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-400"
                        placeholder="mis: social, tts, moodboard"
                        value={newItem.key}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            key: e.target.value,
                          }))
                        }
                      />
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Route otomatis:{" "}
                        <code className="bg-slate-100 px-1 py-0.5 rounded">
                          {autoRoutePreview}
                        </code>
                      </p>
                    </div>

                    {/* Label */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-600">
                        Label (Tampilan)
                      </label>
                      <input
                        type="text"
                        className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-400"
                        placeholder="mis: Social Media Generator"
                        value={newItem.label}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            label: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Feature Code */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-600">
                        Feature Code (unik)
                      </label>
                      <input
                        type="text"
                        className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-50"
                        placeholder="mis: SOCIAL_FEATURE"
                        value={newItem.feature_code}
                        disabled={modalMode === "edit"}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            feature_code: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Kode ini akan disimpan di tabel <code>features</code> dan
                        dipakai di <code>sidebar_items.feature_code</code>.{" "}
                        <span className="text-amber-600">
                          (Di-edit di sini hanya nama & deskripsi fitur, bukan
                          codenya.)
                        </span>
                      </p>
                    </div>

                    {/* Feature Name */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-600">
                        Nama Fitur
                      </label>
                      <input
                        type="text"
                        className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-400"
                        placeholder="mis: Halaman Social Media"
                        value={newItem.feature_name}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            feature_name: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Feature Description */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-600">
                        Deskripsi Fitur (opsional)
                      </label>
                      <textarea
                        rows={2}
                        className="px-2 py-1 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:ring-1 focus:ring-sky-400 resize-none"
                        placeholder="Penjelasan singkat fitur ini..."
                        value={newItem.feature_description}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            feature_description: e.target.value,
                          }))
                        }
                      />
                    </div>

                    {/* Upload icon */}
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] font-medium text-slate-600">
                        Icon (upload saja, ≤ 300 KB)
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center justify-center px-2.5 h-8 rounded-lg text-[11px] font-semibold bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 cursor-pointer">
                          Upload Icon
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleIconFileChange}
                          />
                        </label>
                        {newIconPreview && (
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-slate-500">
                              Preview:
                            </span>
                            <img
                              src={newIconPreview}
                              alt="icon"
                              className="w-6 h-6 rounded border border-slate-200 bg-slate-50 object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Path disimpan ke DB:{" "}
                        <code className="bg-slate-100 px-1 py-0.5 rounded">
                          {newItem.icon_url || "https://example.com/icons/social.png"}
                        </code>
                      </p>
                    </div>
                  </div>

                  {/* Aktif */}
                  <div className="flex items-center gap-3 mt-2">
                    <label className="flex items-center gap-2 text-[12px] text-slate-700">
                      <input
                        type="checkbox"
                        checked={newItem.is_active}
                        onChange={(e) =>
                          setNewItem((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      Aktif
                    </label>
                  </div>
                </div>

                {/* Footer modal */}
                <div className="px-4 sm:px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                  <div /> {/* kiri kosong */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-3 h-8 rounded-lg border border-slate-200 text-[12px] text-slate-600 hover:bg-slate-50"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="
                        inline-flex items-center justify-center
                        px-3 h-8 rounded-lg text-[12px] font-semibold
                        bg-sky-600 text-white
                        hover:bg-sky-700
                        disabled:opacity-60 disabled:cursor-not-allowed
                        transition
                      "
                    >
                      {modalMode === "create"
                        ? "Tambah Sidebar Item"
                        : "Simpan Perubahan"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSidebarItemsPage;
